import { NgIf } from '@angular/common';
import {
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  Inject,
  OnDestroy,
  OnInit,
  ViewChild,
} from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogModule, MatDialogRef } from '@angular/material/dialog';

import { MatLegacyProgressSpinnerModule } from '@angular/material/legacy-progress-spinner';
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';
import { TranslateModule } from '@ngx-translate/core';
import { AccessibleClickDirective } from '@shared/shared/directives/accessible-click.directive';
import Player from '@vimeo/player';
import { Angulartics2Segment } from 'angulartics2';

@Component({
  selector: 'ten-welcome-video',
  templateUrl: './welcome-video.component.html',
  styleUrls: ['./welcome-video.component.scss'],
  changeDetection: ChangeDetectionStrategy.Default,
  standalone: true,
  imports: [NgIf, MatLegacyProgressSpinnerModule, AccessibleClickDirective, TranslateModule, MatDialogModule],
})
export class WelcomeVideoComponent implements OnInit, AfterViewInit, OnDestroy {
  public heroVideo: SafeResourceUrl;
  public playedFull = false;
  public youtubeVideoId: string;
  private player: YT.Player;
  private videoEnded = () => (this.playedFull = true);
  private onPlayerStateChange = (event: YT.OnStateChangeEvent) =>
    event.data === YT.PlayerState.ENDED ? this.videoEnded() : '';
  private onVideoReady = () => (this.loading = false);
  public loading = true;

  @ViewChild('iframeVideo', { static: false })
  public iframeVideo: ElementRef;

  @ViewChild('wrapperDiv', { static: false })
  public wrapperDiv: ElementRef;

  constructor(
    @Inject(MAT_DIALOG_DATA) public data: string,
    public dialogRef: MatDialogRef<WelcomeVideoComponent>,
    public domSanitizer: DomSanitizer,
    private angulartics2Segment: Angulartics2Segment
  ) {}

  public ngOnInit() {
    const separator = this.data.indexOf('?') !== -1 ? '&' : '?';
    this.heroVideo = this.domSanitizer.bypassSecurityTrustResourceUrl(
      `${this.data}${separator}autoplay=1&muted=1&controls=1`
    );
    const matches = this.data.match(/(https:\/\/www.youtube.com\/embed)\/([a-zA-Z0-9]*)/);
    this.youtubeVideoId = matches?.length === 3 ? matches[2] : '';
    this.loading = !!this.youtubeVideoId;
  }

  public closePopup() {
    this.angulartics2Segment.eventTrack('Welcome video Skip button clicked', { playedFull: this.playedFull });
    this.dialogRef.close();
  }

  public ngAfterViewInit() {
    if (this.youtubeVideoId) {
      this.player = new YT.Player(this.wrapperDiv.nativeElement, {
        videoId: this.youtubeVideoId,
        width: '640',
        height: '360',
        playerVars: {
          playsinline: 1,
          autoplay: 1,
          rel: 0,
          controls: 1,
          modestbranding: 1,
          showinfo: 0,
        },
        events: {
          onReady: function (e) {
            e.target.mute();
          },
        },
      });
      this.listenToYoutubeVideoEvents(this.player);
    } else {
      if (this.iframeVideo?.nativeElement) {
        const player = new Player(this.iframeVideo.nativeElement);
        this.listenToVimeoVideoEvents(player);
      }
    }
  }

  private listenToVimeoVideoEvents(player: Player) {
    player.on('ended', () => this.videoEnded());
    player.on('loaded', () => this.onVideoReady());
  }

  private listenToYoutubeVideoEvents(player: YT.Player) {
    player.addEventListener('onStateChange', this.onPlayerStateChange);
    player.addEventListener('onReady', this.onVideoReady);
  }

  public ngOnDestroy() {
    this.player?.destroy();
  }
}
